/* global axios */
// Estúdio Social: redes conectadas via Zernio (Task 10) + posts do calendário
// (Tasks 11-13 — composer/geração por IA, calendário, métricas). Um client só
// porque as duas rotas do backend vivem sob o mesmo namespace `social` e vão
// ser consumidas juntas nas próximas tasks (ex.: SocialAccountsPage já
// precisa saber quais redes existem antes do composer oferecer plataformas).
import ApiClient from './ApiClient';

class SocialAPI extends ApiClient {
  constructor() {
    super('social', { accountScoped: true });
  }

  get accountsUrl() {
    return `${this.url}/accounts`;
  }

  get postsUrl() {
    return `${this.url}/posts`;
  }

  // -- Contas conectadas (Task 10) --------------------------------------

  // { accounts: [{id, platform, username, status, created_at}], limit, connected_count }
  accounts() {
    return axios.get(this.accountsUrl);
  }

  // -> { url } | 422 (limite do plano/plataforma não suportada) | 402 (módulo
  // não ativo, tratado globalmente pelo interceptor) | 429/503 (Zernio).
  connectUrl(platform) {
    return axios.post(`${this.accountsUrl}/connect_url`, { platform });
  }

  disconnect(id) {
    return axios.delete(`${this.accountsUrl}/${id}`);
  }

  // -- Conexão headless (white-label): a rede redireciona pro NOSSO domínio
  // com step+tempToken; a seleção de página é tela nossa. ----------------
  connectOptions(params) {
    return axios.get(`${this.accountsUrl}/connect_options`, { params });
  }

  connectFinalize(payload) {
    return axios.post(`${this.accountsUrl}/connect_finalize`, payload);
  }

  // Reconciliação sob demanda (botão "Atualizar"): busca as contas direto na
  // Zernio e upserta localmente — mesmo shape de resposta do accounts().
  syncAccounts() {
    return axios.post(`${this.accountsUrl}/sync`);
  }

  // -- Posts do calendário (Tasks 11-13) ---------------------------------

  // { from, to } (datas ISO) filtram o range do calendário; o backend usa o
  // mês corrente como default quando omitidos.
  posts({ from, to } = {}) {
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    return axios.get(this.postsUrl, { params });
  }

  getPost(id) {
    return axios.get(`${this.postsUrl}/${id}`);
  }

  // payload: { content, scheduledAt, platforms: [socialAccountId...], media }
  // `media` (File), quando presente, vira multipart.
  productionPosts() {
    return axios.get(this.postsUrl, { params: { scope: 'production' } });
  }

  createPost(payload) {
    return this.postWithMedia(this.postsUrl, 'post', payload);
  }

  updatePost(id, payload) {
    return this.postWithMedia(`${this.postsUrl}/${id}`, 'patch', payload);
  }

  deletePost(id) {
    return axios.delete(`${this.postsUrl}/${id}`);
  }

  // Brief guiado do Criação 2.0. Imagem de referência usa multipart;
  // sem arquivo mantém JSON e o contrato v1 continua aceito pelo backend.
  generatePost(payload = {}) {
    const body = {
      objetivo: payload.objective,
      promover: payload.promote,
      oferta_cta: payload.offerCta,
      tom: payload.tone,
      formato: payload.format,
      cards: payload.cards,
      redes: payload.networks,
      generate_image: payload.generateImage,
    };
    if (!payload.referenceImage) {
      return axios.post(`${this.postsUrl}/generate`, body);
    }

    const form = new FormData();
    Object.entries(body).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (key === 'redes') {
        value.forEach(network => form.append('redes[]', network));
      } else {
        form.append(key, value);
      }
    });
    form.append('reference_image', payload.referenceImage);
    return axios.post(`${this.postsUrl}/generate`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  chooseVariation(id, index) {
    return axios.patch(`${this.postsUrl}/${id}/choose_variation`, { index });
  }

  approvePost(id) {
    return axios.post(`${this.postsUrl}/${id}/approve`);
  }

  publishPost(id) {
    return axios.post(`${this.postsUrl}/${id}/publish`);
  }

  // Relatório de uma rede (IG/FB) no período: { accounts, selected_id, report }.
  metricsOverview({ socialAccountId, days } = {}) {
    const params = {};
    if (socialAccountId) params.social_account_id = socialAccountId;
    if (days) params.days = days;
    return axios.get(`${this.url}/metrics/overview`, { params });
  }

  // Enfileira a sincronização agora. 202 ou 429 (já pedido nos últimos 15 min).
  // Com a rede do X: lê na hora e cobra cada leitura → 200 { reads, charged_brl }
  // ou 402 { error } sem créditos.
  refreshMetrics({ socialAccountId } = {}) {
    const body = socialAccountId ? { social_account_id: socialAccountId } : {};
    return axios.post(`${this.url}/metrics/refresh`, body);
  }

  // eslint-disable-next-line class-methods-use-this
  postWithMedia(
    url,
    method,
    {
      content,
      scheduledAt,
      platforms,
      media,
      title,
      productionStatus,
      driveLink,
      briefing,
    } = {}
  ) {
    if (!media) {
      return axios[method](url, {
        content,
        scheduled_at: scheduledAt,
        platforms,
        title,
        production_status: productionStatus,
        drive_link: driveLink,
        briefing,
      });
    }

    const form = new FormData();
    if (content !== undefined) form.append('content', content || '');
    if (title !== undefined) form.append('title', title || '');
    if (productionStatus !== undefined)
      form.append('production_status', productionStatus || '');
    if (driveLink !== undefined) form.append('drive_link', driveLink || '');
    if (briefing !== undefined) form.append('briefing', briefing || '');
    if (scheduledAt !== undefined) {
      form.append('scheduled_at', scheduledAt || '');
    }
    if (platforms !== undefined) {
      if (platforms.length === 0) {
        // FormData não manda a chave `platforms` quando o array é vazio
        // (forEach de array vazio não gera nenhum append) — sem esse
        // marcador o controller nunca recebe `params[:platforms]`, acha
        // que o campo não veio no request e pula a atualização de redes
        // por completo (mantém as antigas). Manda um item vazio só pra a
        // chave existir; o controller descarta valores em branco antes de
        // resolver os ids (ver posts_controller.rb#resolved_platforms).
        form.append('platforms[]', '');
      } else {
        platforms.forEach(id => form.append('platforms[]', id));
      }
    }
    form.append('media', media);

    return axios[method](url, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }
}

export default new SocialAPI();
